<script setup>
import { storeToRefs } from "pinia";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import useCotizacionEstandarDatosStore from "@/store/cotizaciones/estandar/datos";
import { useCotizacionEstandarStore } from "@/store/cotizaciones/estandar/";
import useUtil from "@/composables/useUtil";
import { useCotizacionEstandarFormato } from "@/composables/useCotizacionEstandar";
import logo from "@/assets/img/Ventus/logo-horizontal.png";
const { montoFinanciar, montoFinanciarIVA } = storeToRefs(
  useCotizacionEstandarDatosStore()
);
const {
  calculaValorResidualCalculo,
  calculaRentaMensual,
  precioFinal,
} = useCotizacionEstandarDatosStore();
const {
  stateCalculosLeaseParameters,
  state,
  stateCotizacion,
  usuarioLoggeado: usuario,
} = storeToRefs(useCotizacionEstandarStore());

const {
  costoFinancieroAnualConBeneficio,
  costoFinancieroAnualSinBeneficio,
  totaPagoInicial,
  valorResidual,
  mostrarPlazos,
  plazos,
  totalRentas,
  totalEgresos,
  totalGastosIniciales,
  totalEgresosMasDeducible,
} = useCotizacionEstandarFormato();

const { formatoMoneda, obtenerFechaActual, agregarDias } = useUtil();

const descargarFormato = async () => {
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageWidth = pdf.internal.pageSize.getWidth();

  const renderElemento = async (elementId, escala) => {
    const element = document.getElementById(elementId);
    if (!element) return;

    const canvas = await html2canvas(element, {
      scale: escala,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.98);
    const imgWidth = (canvas.width * 25.4) / 96;
    const imgHeight = (canvas.height * 25.4) / 96;

    const x = (pageWidth - imgWidth) / 2;
    const y = 0; // Desde arriba, sin margen superior

    pdf.addImage(imgData, "JPEG", x, y, imgWidth, imgHeight);
  };

  await renderElemento("formato", 0.75);
  pdf.addPage();
  await renderElemento("formato2", 0.7);

  pdf.save("formato.pdf");
};
</script>

<template>
  <button class="btn btn-success" @click="descargarFormato">Descargar</button>
  <div class="mb-3">
    <label v-for="plazo in plazos" :key="'chk-' + plazo" class="me-3">
      <input type="checkbox" v-model="mostrarPlazos[plazo]" />
      {{ plazo }} meses
    </label>
  </div>
  <section id="formato">
    <div class="d-flex align-items-center">
      <div class="col-md-6">
        <img :src="logo" width="200" />
      </div>
      <div class="col-md-6">
        <h5 class="text-uppercase">Cotización</h5>
      </div>
    </div>
    <div class="row text-dark">
      <div class="col-md-6">
        <div class="bg-ventus py-2 px-5 bg-ventus w-fit text-dark rounded-3">
          Datos del Cliente
        </div>
        <div class="py-3 info">
          <p>
            <b class="me-1">Nombre:</b>
            <span class="text-uppercase">{{
              state.cliente_id?.label ?? ""
            }}</span>
          </p>
          <p>
            <b class="me-1">Correo:</b>
            <span class="text-lowercase">{{ state.cliente_id?.correo }}</span>
          </p>
          <p>
            <b class="me-1">Teléfono:</b>
            <span class="text-uppercase">{{ state.cliente_id?.celular }}</span>
          </p>
        </div>
      </div>
      <div class="col-md-6">
        <div class="bg-ventus py-2 px-4 bg-ventus w-fit text-dark rounded-3">
          Datos del Agente Comercial
        </div>
        <div class="py-3 info">
          <p>
            <b class="me-1">Nombre:</b>
            <span class="text-uppercase"
              >{{ usuario.nombre }} {{ usuario.paterno }}
              {{ usuario.materno }}</span
            >
          </p>
          <p><b class="me-1">Correo:</b> {{ usuario.email }}</p>
          <p><b class="me-1">Teléfono:</b> {{ usuario.celular }}</p>
        </div>
      </div>
    </div>
    <div class="row text-dark">
      <div class="col-md-6">
        <table class="w-100">
          <tr>
            <th class="text-nowrap align-middle">Tipo de operación</th>
            <td class="w-100">
              <div class="bg-terciary rounded-3 mx-3 p-1">
                Arrendamiento Puro
              </div>
            </td>
          </tr>
          <tr>
            <th class="text-nowrap align-middle">Valor del bien arrendado</th>
            <td class="w-100">
              <div class="bg-terciary rounded-3 mx-3 p-1">
                {{ formatoMoneda(precioFinal) }}
              </div>
            </td>
          </tr>
          <tr>
            <th class="text-nowrap align-middle">Tipo de equipo</th>
            <td class="w-100">
              <div class="bg-terciary rounded-3 mx-3 p-1">
                {{ state?.arrendamiento ?? "-" }}
              </div>
            </td>
          </tr>
          <tr>
            <th class="text-nowrap align-middle">Descripción</th>
            <td class="w-100">
              <div class="bg-terciary rounded-3 mx-3 p-1 text-uppercase">
                {{ state?.descripcion ?? "-" }}
              </div>
            </td>
          </tr>
          <tr>
            <th class="text-nowrap align-middle">Moneda</th>
            <td class="w-100">
              <div class="bg-terciary rounded-3 mx-3 p-1">
                {{ state.monedaOperacion }}
              </div>
            </td>
          </tr>
        </table>
      </div>
      <div class="col-md-6">
        <table class="w-100">
          <tr>
            <th class="text-nowrap align-middle">Fecha de cotización</th>
            <td class="w-100">
              <div class="bg-dark rounded-3 mx-3 p-1 text-white">
                {{ obtenerFechaActual() }}
              </div>
            </td>
          </tr>
          <tr>
            <th class="text-nowrap align-middle">Fecha de vencimiento</th>
            <td class="w-100">
              <div class="bg-dark rounded-3 mx-3 p-1 text-white">
                {{ agregarDias(obtenerFechaActual(), 30) }}
              </div>
            </td>
          </tr>
        </table>
      </div>
    </div>
    <div>
      <h6 class="text-center mt-6 mb-4 text-uppercase">
        Condiciones de arrendamiento
      </h6>
      <div>
        <table class="table text-center text-dark">
          <thead>
            <tr class="border-row">
              <th class="bg-dark text-white rounded-top-xl">Conceptos</th>
              <th
                v-for="plazo in plazos"
                :key="'th-' + plazo"
                :class="[
                  plazo % 24 === 0 ? 'bg-ventus' : 'bg-light',
                  { 'd-none': !mostrarPlazos[plazo] },
                ]"
              >
                {{ plazo }} meses
              </th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-row">
              <th colspan="6" class="text-start">RENTA MENSUAL</th>
            </tr>
            <tr class="border-row">
              <td>Valor de la renta + iva</td>
              <td
                v-for="plazo in plazos"
                :key="'renta-' + plazo"
                :class="[
                  plazo % 24 === 0 ? 'bg-ventus' : 'bg-light',
                  { 'd-none': !mostrarPlazos[plazo] },
                ]"
              >
                {{ formatoMoneda(calculaRentaMensual(plazo)) }}
              </td>
            </tr>

            <tr class="border-row">
              <th colspan="6" class="text-start">PAGO INICIAL</th>
            </tr>
            <tr class="border-row">
              <td>Total pago inicial + iva</td>
              <td
                v-for="plazo in plazos"
                :key="'pago-inicial-' + plazo"
                :class="[
                  plazo % 24 === 0 ? 'bg-ventus' : 'bg-light',
                  { 'd-none': !mostrarPlazos[plazo] },
                ]"
              >
                {{ totaPagoInicial(plazo) }}
              </td>
            </tr>

            <tr class="border-row">
              <th colspan="6" class="text-start">RESIDUAL</th>
            </tr>
            <tr class="border-row">
              <td>Valor Residual</td>
              <td
                v-for="plazo in plazos"
                :key="'residual-' + plazo"
                :class="[
                  plazo % 24 === 0 ? 'bg-ventus' : 'bg-light',
                  { 'd-none': !mostrarPlazos[plazo] },
                ]"
              >
                {{ valorResidual(plazo) }}
              </td>
            </tr>
          </tbody>
        </table>

        <p class="small mt-7 mb-4">
          1.La presente cotización es de carácter informativo y requiere de
          aprobación y puede ser modificada en cualquier momento.<br />
          2.Las cantidades expresadas son en la moneda de la operación y no
          incluyen el IVA, por lo tanto deberá de ser adicionado en cada
          concepto (en los que aplique).<br />
          3.El tipo de cambio se ajustará al día de la operación, el utilizado
          en está cotización es con fines informativos.<br />
          4.En caso de “Sale & Lease Back” se requiere avalúo sobre el equipo a
          arrendar. El costo del avalúo deberá ser cubierto por parte del
          cliente.<br />
          5.Está cotización no incluye seguro ni tampoco los gastos legales, los
          cuales deberán de ser cotizados más adelante.<br />
          6.En montos a financiar menores a los $80,000 USD (o su equivalente en
          pesos), el plazo máximo es de 48 meses.<br />
          7.Vigencia de la propuesta: 30 días a partir de hoy.
        </p>
        <hr />
      </div>
    </div>
  </section>
  <section id="formato2">
    <div class="d-flex align-items-center">
      <div class="col-md-6">
        <img :src="logo" width="200" />
      </div>
      <div class="col-md-6">
        <h6 class="text-uppercase">
          Analisis de Costo Financiero<br />& Simulación Beneficio Fiscal
        </h6>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <table class="table text-dark" cellspacing="10">
          <thead>
            <tr>
              <th>Cotización de:</th>
              <th>
                <span class="bg-light px-6 py-2 rounded-3"
                  >Arrendamiento Puro</span
                >
              </th>
              <th>Plazo:</th>
              <th>
                <span class="bg-light px-6 py-2 rounded-3"
                  >{{ stateCotizacion.plazo }} meses</span
                >
              </th>
            </tr>
          </thead>
        </table>
        <table class="table text-dark text-center small tabla-espaciada">
          <thead>
            <tr>
              <th width="400" class="bg-dark text-white text-start">
                CONCEPTOS
              </th>
              <th colspan="3" class="bg-ventus text-white text-start">
                EGRESOS TOTALES
              </th>
            </tr>
            <tr>
              <th></th>
              <th>NETO</th>
              <th>IVA</th>
              <th>TOTAL</th>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-light">VALOR DEL EQUIPO</td>
              <td class="bg-light">{{ formatoMoneda(precioFinal) }}</td>
              <td class="bg-light">
                {{ formatoMoneda(precioFinal * 0.16) }}
              </td>
              <td class="bg-light">
                {{ formatoMoneda(precioFinal * 1.16) }}
              </td>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-light">ANTICIPO A RENTAS</td>
              <td class="bg-light">
                {{
                  formatoMoneda(
                    stateCotizacion[`enganche_${stateCotizacion.plazo}`]
                  )
                }}
              </td>
              <td class="bg-light">
                {{
                  formatoMoneda(
                    stateCotizacion[`enganche_${stateCotizacion.plazo}`] * 0.16
                  )
                }}
              </td>
              <td class="bg-light">
                {{
                  formatoMoneda(
                    stateCotizacion[`enganche_${stateCotizacion.plazo}`] * 1.16
                  )
                }}
              </td>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-total">
                MONTO A FINANCIAR ({{ state.monedaOperacion }})
              </td>
              <td class="bg-total">{{ formatoMoneda(montoFinanciar) }}</td>
              <td class="bg-total">
                {{ formatoMoneda(montoFinanciar * 0.16) }}
              </td>
              <td class="bg-total">{{ formatoMoneda(montoFinanciarIVA) }}</td>
            </tr>
            <tr>
              <th colspan="4" class="text-start">GASTOS INICIALES</th>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-light">SEGURO</td>
              <td class="bg-light">
                {{ formatoMoneda(stateCotizacion?.seguro ?? 0) }}
              </td>
              <td class="bg-light">
                {{ formatoMoneda(stateCotizacion.seguro * 0.16) }}
              </td>
              <td class="bg-light">
                {{ formatoMoneda(stateCotizacion.seguro * 1.16) }}
              </td>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-light">GASTOS LEGALES</td>
              <td class="bg-light">
                {{ formatoMoneda(stateCotizacion.gastosLegales) }}
              </td>
              <td class="bg-light">
                {{ formatoMoneda(stateCotizacion.gastosLegales * 0.16) }}
              </td>
              <td class="bg-light">
                {{ formatoMoneda(stateCotizacion.gastosLegales * 1.16) }}
              </td>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-total">GASTOS DE IMPORTACION</td>
              <td class="bg-total">
                {{ formatoMoneda(stateCotizacion.gastosImportacion) }}
              </td>
              <td class="bg-total">
                {{ formatoMoneda(stateCotizacion.gastosImportacion * 0.16) }}
              </td>
              <td class="bg-total">
                {{ formatoMoneda(stateCotizacion.gastosImportacion * 1.16) }}
              </td>
            </tr>
            <tr>
              <th class="text-start">
                <small
                  >(1) Pendiente cotizar, Gastos de importación incluye Flete,
                  Seguro y Agente Aduanal</small
                ><br />
                RENTA MENSUAL
              </th>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-light">VALOR DE RENTA MENSUAL</td>
              <td class="bg-light">
                {{ formatoMoneda(calculaRentaMensual(stateCotizacion.plazo)) }}
              </td>
              <td class="bg-light">
                {{
                  formatoMoneda(
                    calculaRentaMensual(stateCotizacion.plazo) * 0.16
                  )
                }}
              </td>
              <td class="bg-light">
                {{
                  formatoMoneda(
                    calculaRentaMensual(stateCotizacion.plazo) * 1.16
                  )
                }}
              </td>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-total">
                TOTAL DE RENTAS ({{ stateCotizacion.plazo - 1 }})
              </td>
              <td class="bg-total">
                {{ formatoMoneda(totalRentas(stateCotizacion.plazo)) }}
              </td>
              <td class="bg-total">
                {{ formatoMoneda(totalRentas(stateCotizacion.plazo) * 0.16) }}
              </td>
              <td class="bg-total">
                {{ formatoMoneda(totalRentas(stateCotizacion.plazo) * 1.16) }}
              </td>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-total">VALOR RESIDUAL</td>
              <td class="bg-total">
                {{
                  formatoMoneda(
                    calculaValorResidualCalculo(stateCotizacion.plazo)
                  )
                }}
              </td>
              <td class="bg-total">
                {{
                  formatoMoneda(
                    calculaValorResidualCalculo(stateCotizacion.plazo) * 0.16
                  )
                }}
              </td>
              <td class="bg-total">
                {{
                  formatoMoneda(
                    calculaValorResidualCalculo(stateCotizacion.plazo) * 1.16
                  )
                }}
              </td>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-dark text-white">TOTAL EGRESOS</td>
              <td class="bg-dark text-white">
                {{ formatoMoneda(totalEgresos) }}
              </td>
              <td class="bg-dark text-white">
                {{ formatoMoneda(totalEgresos * 0.16) }}
              </td>
              <td class="bg-dark text-white">
                {{ formatoMoneda(totalEgresos * 1.16) }}
              </td>
            </tr>
            <tr>
              <th class="text-start">
                <small>(Gastos Iniciales + Rentas + Residual)</small>
              </th>
              <th colspan="2" class="text-end">Gastos Neto vs Equipo</th>
              <th>{{ formatoMoneda(totalEgresos - precioFinal) }}</th>
            </tr>
            <tr>
              <th></th>
              <th colspan="2" class="text-end">
                Costo Financiero Anual (SIN Beneficio Fiscal)
              </th>
              <th>
                {{
                  formatoMoneda(costoFinancieroAnualSinBeneficio * 100, false)
                }}%
              </th>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-light">GASTOS INICIALES</td>
              <td class="bg-light">
                {{ formatoMoneda(totalGastosIniciales) }}
              </td>
              <td class="bg-light">
                {{ formatoMoneda(totalGastosIniciales * 0.16) }}
              </td>
              <td class="bg-light">
                {{ formatoMoneda(totalGastosIniciales * 1.16) }}
              </td>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-total">TOTAL DE RENTAS</td>
              <td class="bg-total">
                {{ formatoMoneda(totalRentas(stateCotizacion.plazo)) }}
              </td>
              <td class="bg-total">
                {{ formatoMoneda(totalRentas(stateCotizacion.plazo) * 0.16) }}
              </td>
              <td class="bg-total">
                {{ formatoMoneda(totalRentas(stateCotizacion.plazo) * 1.16) }}
              </td>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-total">TOTAL DEDUCIBLE</td>
              <td class="bg-total">
                {{
                  formatoMoneda(
                    totalGastosIniciales + totalRentas(stateCotizacion.plazo)
                  )
                }}
              </td>
              <td class="bg-total">
                {{
                  formatoMoneda(
                    (totalGastosIniciales +
                      totalRentas(stateCotizacion.plazo)) *
                      0.16
                  )
                }}
              </td>
              <td class="bg-total">
                {{
                  formatoMoneda(
                    (totalGastosIniciales +
                      totalRentas(stateCotizacion.plazo)) *
                      1.16
                  )
                }}
              </td>
            </tr>
            <tr>
              <th class="text-start">
                <small>Beneficio Fiscal (estimado 30%)</small>
              </th>
              <th>
                {{
                  formatoMoneda(
                    (totalGastosIniciales +
                      totalRentas(stateCotizacion.plazo)) *
                      -0.3
                  )
                }}
              </th>
              <th colspan="2"></th>
            </tr>
            <tr class="fila-con-espaciado">
              <td class="text-start bg-dark text-white">
                TOTAL EGRESOS + DEDUCIBLES
              </td>
              <td class="bg-dark text-white">
                {{ formatoMoneda(totalEgresosMasDeducible) }}
              </td>
              <td class="bg-dark text-white">
                {{ formatoMoneda(totalEgresosMasDeducible * 0.16) }}
              </td>
              <td class="bg-dark text-white">
                {{ formatoMoneda(totalEgresosMasDeducible * 1.16) }}
              </td>
            </tr>
            <tr>
              <th class="text-start">
                <small>(Gastos Iniciales + Rentas + Residual- Deducible)</small>
              </th>
              <th colspan="2" class="text-end">Gastos Neto vs Equipo</th>
              <th>
                {{ formatoMoneda(totalEgresosMasDeducible - precioFinal) }}
              </th>
            </tr>
            <tr>
              <th></th>
              <th colspan="2" class="text-end">
                Costo Financiero Anual (SIN Beneficio Fiscal)
              </th>
              <th>
                {{
                  formatoMoneda(costoFinancieroAnualConBeneficio * 100, false)
                }}%
              </th>
            </tr>
          </thead>
        </table>
      </div>
    </div>
  </section>
</template>

<style scoped>
.bg-ventus {
  background: #efc233;
}

.bg-light {
  background: #e2e2e6;
}

.bg-terciary {
  background: #ecebe5;
}

.text-dark {
  color: #000 !important;
}

.w-fit {
  width: fit-content;
}

.info p {
  line-height: 0.7;
}

.bg-dark {
  background: #000 !important;
}

.rounded-top-xl {
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
}

.border-row {
  border-bottom: 2px solid #000;
}

.tabla-espaciada {
  border-collapse: separate;
  border-spacing: 10px; /* espacio entre celdas */
}

.tabla-espaciada td {
  border: 1px dashed #bbb;
}

.bg-total {
  background: #fff2ad;
}

#formato,
#formato2 {
  transform-origin: top left;
  width: 100%;
}

#formato {
  /* transform: scale(0.9) translateX(5.5%); */
}

#formato2 {
  /* transform: scale(0.7) translateX(10.5%); */
}
</style>
